import React from 'react';
import { AssetViewer } from '@/components/VisualAssetEditor/AssetViewer';
import { VisualAssetData } from '@/components/VisualAssetEditor/types';

// Sample test data with accessible public images
const createTestData = (viewMode: string, aspectRatio: string): VisualAssetData => ({
  aspectRatio,
  viewMode,
  comparison_mode: viewMode,
  header: `Test ${viewMode} - ${aspectRatio}`,
  subheader: `Testing ${viewMode} view mode with ${aspectRatio} aspect ratio`,
  companyName: 'Test Company',
  companyLogo: '',
  primaryImage: 'https://picsum.photos/800/600?random=1',
  secondaryImage: 'https://picsum.photos/800/600?random=2',
  primaryImages: [
    { version: '20250101', url: 'https://picsum.photos/800/600?random=1' }
  ],
  secondaryImages: [
    { version: '20250706', url: 'https://picsum.photos/800/600?random=2' }
  ],
  selectedPrimaryVersion: '20250101',
  selectedSecondaryVersion: '20250706'
});

export default function AssetViewerTest() {
  const viewModes = ['single', 'side_by_side', 'stacked'];
  const aspectRatios = ['1:1', '16:9', '4:3'];

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-center mb-8">AssetViewer Test - All Permutations</h1>
        <p className="text-center text-gray-600 mb-12">
          Testing all 9 combinations of view modes (single, side_by_side, stacked) × aspect ratios (1:1, 16:9, 4:3)
        </p>

        {/* Grid of all permutations - Auto-height containers */}
        <div className="flex flex-wrap gap-16 justify-center">
          {aspectRatios.map((aspectRatio) =>
            viewModes.map((viewMode) => (
              <div key={`${aspectRatio}-${viewMode}`} className="w-[500px] h-[700px] bg-gray-50 rounded-lg p-8 relative">
                <AssetViewer
                  data={{
                    ...createTestData(viewMode, aspectRatio),
                    header: `${viewMode.replace('_', ' ')} • ${aspectRatio}`,
                    subheader: `Testing ${viewMode} view with ${aspectRatio} aspect ratio`
                  }}
                  className="w-full h-full"
                  showHeader={true}
                  showFooter={true}
                />
              </div>
            ))
          )}
        </div>

        {/* Additional test cases - Direct asset cards */}
        <div className="mt-16">
          <h2 className="text-2xl font-semibold text-center mb-8">Additional Test Cases</h2>

          <div className="flex flex-wrap gap-16 justify-center">
            {/* No header test */}
            <div className="w-[500px] h-[700px] bg-gray-50 rounded-lg p-8 relative">
              <AssetViewer
                data={{
                  ...createTestData('stacked', '16:9'),
                  header: '',
                  subheader: 'No Header Test'
                }}
                className="w-full h-full"
                showHeader={false}
                showFooter={true}
              />
            </div>

            {/* No footer test */}
            <div className="w-[500px] h-[700px] bg-gray-50 rounded-lg p-8 relative">
              <AssetViewer
                data={{
                  ...createTestData('side_by_side', '1:1'),
                  header: 'No Footer Test',
                  subheader: 'Side by Side • 1:1'
                }}
                className="w-full h-full"
                showHeader={true}
                showFooter={false}
              />
            </div>

            {/* No header or footer test */}
            <div className="w-[500px] h-[700px] bg-gray-50 rounded-lg p-8 relative">
              <AssetViewer
                data={createTestData('single', '4:3')}
                className="w-full h-full"
                showHeader={false}
                showFooter={false}
              />
            </div>

            {/* Large container test */}
            <div className="w-[500px] h-[700px] bg-gray-50 rounded-lg p-8 relative">
              <AssetViewer
                data={{
                  ...createTestData('stacked', '1:1'),
                  header: 'Large Container Test',
                  subheader: 'Stacked • 1:1 • Custom Size'
                }}
                className="w-full h-full"
                showHeader={true}
                showFooter={true}
                width={1200}
                height={800}
              />
            </div>
          </div>
        </div>

        {/* Responsive test section */}
        <div className="mt-24">
          <h2 className="text-2xl font-semibold text-center mb-8">Responsive Test</h2>
          <div className="flex justify-center">
            <div className="w-[900px] h-[700px] bg-gray-50 rounded-lg p-8 relative">
              <AssetViewer
                data={{
                  ...createTestData('side_by_side', '16:9'),
                  header: 'Full Width Container Test',
                  subheader: 'Side by Side • 16:9 • Responsive'
                }}
                className="w-full h-full"
                showHeader={true}
                showFooter={true}
              />
            </div>
          </div>
        </div>

        {/* Debug info */}
        <div className="mt-16 bg-gray-100 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Test Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h3 className="font-medium mb-2">View Modes Tested:</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>single - Single image display</li>
                <li>side_by_side - Two images side by side</li>
                <li>stacked - Two images stacked vertically</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium mb-2">Aspect Ratios Tested:</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>1:1 - Square format</li>
                <li>16:9 - Widescreen format</li>
                <li>4:3 - Traditional format</li>
              </ul>
            </div>
          </div>
          <div className="mt-4">
            <h3 className="font-medium mb-2">What to Check:</h3>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Images are properly scaled and centered within containers</li>
              <li>Aspect ratios are correctly maintained</li>
              <li>Grid layouts work for all view modes</li>
              <li>Version labels appear correctly</li>
              <li>Headers and footers display properly</li>
              <li>Responsive sizing works across different container sizes</li>
              <li>Borders and spacing look consistent</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}